import type { Location } from "react-router-dom";

const OTP_TYPES = ["recovery", "invite", "email", "magiclink"] as const;
type OtpType = (typeof OTP_TYPES)[number];

const isOtpType = (value: string | null): value is OtpType =>
  value !== null && (OTP_TYPES as readonly string[]).includes(value);

export type AuthLinkToken =
  | { type: "code"; code: string }
  | { type: "hash"; accessToken: string; refreshToken: string; hashType: string | null }
  | { type: "token_hash"; tokenHash: string; otpType: OtpType };

/** Whether confirming this link should gate the recovery-confirm window
 *  (see beginRecoveryConfirm/cancelRecoveryConfirm in authMachine/operations.ts). */
export const isRecoveryToken = (token: AuthLinkToken): boolean =>
  (token.type === "hash" && token.hashType === "recovery") ||
  (token.type === "token_hash" && token.otpType === "recovery");

/** Whether this link is a signup-confirmation or invite link (the "welcome" flow). */
export const isWelcomeToken = (token: AuthLinkToken): boolean =>
  (token.type === "hash" && (token.hashType === "invite" || token.hashType === "email")) ||
  (token.type === "token_hash" && (token.otpType === "invite" || token.otpType === "email"));

/**
 * Parses unconsumed sign-in-link token material out of a URL, without consuming
 * it. Returns null on a plain visit, or when the hash carries a dead-link error
 * (see parseAuthLinkError for that case) instead of a live token.
 */
export const parseAuthLinkToken = (
  location: Pick<Location, "search" | "hash">
): AuthLinkToken | null => {
  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get("code");
  if (code) {
    return { type: "code", code };
  }

  const tokenHash = searchParams.get("token_hash");
  if (tokenHash) {
    const otpType = searchParams.get("type");
    return isOtpType(otpType) ? { type: "token_hash", tokenHash, otpType } : null;
  }

  const hash = location.hash.startsWith("#") ? location.hash.slice(1) : location.hash;
  const hashParams = new URLSearchParams(hash);
  if (hashParams.get("error") || hashParams.get("error_code")) {
    return null;
  }

  const accessToken = hashParams.get("access_token");
  const refreshToken = hashParams.get("refresh_token");
  if (accessToken && refreshToken) {
    return { type: "hash", accessToken, refreshToken, hashType: hashParams.get("type") };
  }

  return null;
};
